// Ultimate Card Game (UCG) - Bot AI card rules
//
// The situational rules the hand scorer (js/engine/ai/handScorer.js) applies
// to ONE specific card instead of to a stat pattern: when a spell may be cast,
// when a structure is worth its plasma, how much a Salvage Company is worth
// while the base is under pressure. Moved here unchanged from the scorer's
// hand-written blocks (Phase 0.5, docs/AI_MASTERPLAN_2026-10.md); the order
// below is the order they always ran in.
//
// A rule is { id, matches(card), score(ctx) }. `ctx` carries the card, its
// hand slot and cost, the threat picture, the plasma the bot may spend, and
// the hand tallies; score(ctx) returns the new score for the card (-999 =
// strictly forbidden right now). Rules match on what defines the card (its
// spell effect, unit type or special property), so a new card with that
// property joins automatically. A card with no rule is scored from its stats
// and the generic counter bonuses alone - adding a card never breaks the bot,
// it just may not be played optimally.
//
// Per-card roles and timing notes (data, for gambits and deck building) live
// in js/data/aiCardHints.js.

// Phase 1: before the "strictly forbidden" early exit, right after the
// stat-derived base score.
export const CARD_RULES = [
  // Economic Salvage Tractor (No weapons - requires frontline protection)
  {
    id: 'salvageCompany',
    matches: (card) => card.name === 'The Salvage Company' || card.unitType === 'TheSalvageCompany',
    score(ctx) {
      const { bot, threats } = ctx;
      let score = ctx.score;
      const baseHpFraction = (bot.battlefield?.bases?.[bot.team]?.hp || 1000) / (bot.battlefield?.bases?.[bot.team]?.maxHp || 1000);
      if (threats.enemyNearBaseCount >= 1 || threats.isPressedBack || baseHpFraction < 0.4) {
        score = -999; // Never invest 50 plasma in unarmored economy when base is threatened or crisis active
      } else if (threats.ownUnits.length >= 2 && !threats.isPressedBack) {
        score += 35; // High value when frontline is secured and holding
      }
      return score;
    }
  },
  // Sul Tank / Reflection Priority (Rejected Loaf)
  {
    id: 'reflection',
    matches: (card) => card.reflectDamageRatio,
    score(ctx) {
      const { card } = ctx;
      let score = ctx.score;
      score += 18 + (card.hp || 0) * 0.12;
      return score;
    }
  },
  // Sul Forced Overstimulation Rush Priority & Anti-Suicide Safeguards
  {
    id: 'forcedOverstimulation',
    matches: (card) => card.spellEffect === 'forced_overstimulation',
    score(ctx) {
      const { bot, threats } = ctx;
      let score = ctx.score;
      const b = bot.cardSystem?.battlefield || bot.battlefield;
      let eligibleUnits = 0;
      let forwardUnits = 0;
      let healthyHeavyUnits = 0;
      let friendlyMobilePlasma = 0;
      let friendlyMobileUnits = 0;
      let overstimulatedUnits = 0;
      let minDistToEnemyBase = Infinity;
      let isEngagedInCombat = false;
      let friendlyNearOwnBase = 0;

      let enemyMobilePlasma = 0;
      let enemyMobileUnits = 0;

      const enemyTeam = bot.team === 'western' ? 'eastern' : 'western';
      const ownBase = b?.bases?.[bot.team] || threats?.ownBase;
      const enemyBase = b?.bases?.[enemyTeam];

      if (b?.units) {
        const midX = (b.width || 1600) / 2;
        for (let ui = 0; ui < b.units.length; ui++) {
          const u = b.units[ui];
          if (u.isDead || u.isCinematic || u.isGoldenStatue || u.isCocoon) continue;

          const uCost = u.cardData?.cost || 10;
          if (u.team === bot.team) {
            if (u.isStructure) continue;
            eligibleUnits++;
            friendlyMobileUnits++;
            friendlyMobilePlasma += uCost;

            if (u.isOverstimulated || (u.overstimulationTimer && u.overstimulationTimer > 0)) {
              overstimulatedUnits++;
            }

            const isPastMid = bot.team === 'western' ? (u.x >= midX) : (u.x <= midX);
            if (isPastMid) forwardUnits++;

            if (enemyBase) {
              const distToBase = Math.abs(u.x - enemyBase.x);
              if (distToBase < minDistToEnemyBase) minDistToEnemyBase = distToBase;
            }

            if (ownBase && Math.abs(u.x - ownBase.x) <= 350) {
              friendlyNearOwnBase++;
            }

            // Detect heavy / tier-3 anchor units that must NOT be sacrificed
            const isHeavy = uCost >= 25 ||
              Boolean(u.isLootingVulture || u.name === 'Looting Vulture' || u.cardData?.unitType === 'LootingVulture') ||
              Boolean(u.isPreserverLock || u.name === 'Preserver Lock' || u.cardData?.unitType === 'PreserverLock') ||
              Boolean(u.isSentinalLock || u.name === 'Sentinal Lock' || u.cardData?.unitType === 'SentinalLock') ||
              Boolean(u.isEnforcerVulture || u.name === 'Enforcer Vulture' || u.cardData?.unitType === 'EnforcerVulture') ||
              Boolean(u.isHyper || u.cardData?.isTitan || (u.name && u.name.startsWith('Hyper')) || (u.name && u.name.startsWith('Titan'))) ||
              Boolean(((u.maxHp || u.hp || 0) >= 180 && uCost >= 20));

            if (isHeavy) {
              const maxHp = u.maxHp || u.hp || 1;
              const hpRatio = (u.hp || 0) / maxHp;
              const isHealthy = hpRatio >= 0.35 || (u.hp || 0) >= 100;
              if (isHealthy) {
                healthyHeavyUnits++;
              }
            }
          } else {
            if (!u.isStructure) {
              enemyMobileUnits++;
              enemyMobilePlasma += uCost;
            }
          }
        }

        // Check if any friendly unit is actively in combat with enemy forces
        for (let fi = 0; fi < b.units.length; fi++) {
          const fu = b.units[fi];
          if (fu.team !== bot.team || fu.isDead || fu.isStructure || fu.isGoldenStatue || fu.isCocoon) continue;
          if (fu.currentTarget && !fu.currentTarget.isDead && fu.currentTarget.team !== bot.team) {
            isEngagedInCombat = true;
            break;
          }
          for (let ei = 0; ei < b.units.length; ei++) {
            const eu = b.units[ei];
            if (eu.team === bot.team || eu.isDead || eu.isStructure || eu.isGoldenStatue || eu.isCocoon) continue;
            if (Math.abs(fu.x - eu.x) <= 250 && Math.abs(fu.y - eu.y) <= 120) {
              isEngagedInCombat = true;
              break;
            }
          }
          if (isEngagedInCombat) break;
        }
      }

      const enemyBaseHp = enemyBase ? (enemyBase.hp !== undefined ? enemyBase.hp : 1000) : 1000;
      const enemyBaseMax = enemyBase ? (enemyBase.maxHp || 1000) : 1000;
      const isEnemyBaseVulnerable = enemyBaseHp <= 600 || (enemyBaseHp / enemyBaseMax <= 0.35);
      const isLethalBaseRush = minDistToEnemyBase <= 320 && isEnemyBaseVulnerable && eligibleUnits >= 2;

      const ownBaseHp = ownBase ? (ownBase.hp !== undefined ? ownBase.hp : 1000) : 1000;
      const ownBaseMax = ownBase ? (ownBase.maxHp || 1000) : 1000;
      const isOwnBaseCritical = ownBaseHp <= 300 || (ownBaseHp / ownBaseMax <= 0.25);
      const isEmergencyDefense = (isOwnBaseCritical || (threats?.enemyNearBaseCount >= 2 && ownBaseHp <= 450)) &&
        (friendlyNearOwnBase >= 1 || eligibleUnits >= 2);

      const isNukeImminent = bot.nuke?.state === 'arming' || (bot.superweapons?.get?.(bot.team) > 0 && bot.nuke?.canFire?.());
      if (isNukeImminent && isEngagedInCombat && eligibleUnits >= 2) {
        score = 85 + eligibleUnits * 5;
        return score;
      }

      // Board advantage evaluation: AI army already has strong advantage on the battlefield
      const hasBoardAdvantage = (friendlyMobilePlasma >= 35 && friendlyMobilePlasma >= enemyMobilePlasma * 1.1) ||
        (friendlyMobileUnits >= 3 && enemyMobileUnits === 0 && friendlyMobilePlasma >= 30);

      // Strict Anti-Suicide Gating:
      // 1. Never cast on less than 3 units unless saving own base in critical defense
      // 2. Never sacrifice healthy heavy units (e.g. 2 Looting Vultures) unless game-winning lethal base rush
      // 3. Never sacrifice an army holding board advantage unless game-winning lethal base rush
      // 4. Never cast if all units are behind mid with no enemy in combat range (empty march into death)
      // 5. Never recast if >= 50% of units are already overstimulated unless lethal rush
      if (!isLethalBaseRush && !isEmergencyDefense) {
        if (
          eligibleUnits < 3 ||
          healthyHeavyUnits > 0 ||
          hasBoardAdvantage ||
          (forwardUnits === 0 && !isEngagedInCombat) ||
          (eligibleUnits > 0 && overstimulatedUnits >= eligibleUnits * 0.5)
        ) {
          score = -999;
        } else {
          score += 20 + eligibleUnits * 8 + (forwardUnits > 0 ? 35 : 0) + (isEngagedInCombat ? 15 : 0);
        }
      } else if (isLethalBaseRush) {
        score = 95 + Math.min(30, eligibleUnits * 6);
      } else if (isEmergencyDefense) {
        score = 80 + Math.min(25, eligibleUnits * 5);
      }
      return score;
    }
  },
  // Sul Claiming Pillar (Territorial Frontline Lock & Income Multiplier)
  {
    id: 'claimingPillar',
    matches: (card) => card.name === 'Claiming Pillar' || card.unitType === 'ClaimingPillar',
    score(ctx) {
      const { bot, threats } = ctx;
      let score = ctx.score;
      const b = bot.cardSystem?.battlefield || bot.battlefield;
      const midX = (b?.width || 1600) / 2;
      const divider = (b?.territoryDivider !== undefined && b?.territoryDivider !== null) ? b.territoryDivider : midX;
      const deadZone = 15;
      const halfWidth = midX - 46;
      const maxDist = Math.max(1, halfWidth - deadZone);
      const isWestern = bot.team === 'western';
      const advDist = isWestern ? (divider - (midX + deadZone)) : ((midX - deadZone) - divider);
      const pushRatio = Math.max(0, Math.min(1, advDist / maxDist));
      const territoryControl = b?.territoryControl || (divider > (midX + deadZone) ? 'western' : divider < (midX - deadZone) ? 'eastern' : 'neutral');
      const hasTerritoryLead = territoryControl === bot.team && pushRatio >= 0.25;

      let alreadyHasPillar = false;
      if (b?.units) {
        for (let ui = 0; ui < b.units.length; ui++) {
          const u = b.units[ui];
          if (u.team === bot.team && (u.isClaimingPillar || u.name === 'Claiming Pillar' || u.cardName === 'Claiming Pillar' || u.unitType === 'ClaimingPillar') && !u.isDead) {
            alreadyHasPillar = true;
            break;
          }
        }
      }

      const isEnemyLock = Boolean(b?.isTerritoryLocked && b?.territoryLockedTeam && b.territoryLockedTeam !== bot.team);

      // Claiming Pillar spawns directly on the indicator line, has 0 attack and 200 HP,
      // and freezes the territory divider. If played at 0% bonus / neutral frontline or
      // while retreating, it self-sabotages territorial advancement and yields 0x bonus.
      // It must only be deployed when holding a solid push (>= 25% deep into enemy territory),
      // with friendly units present to protect it, and never in early opening.
      if (
        !hasTerritoryLead ||
        pushRatio < 0.25 ||
        (bot.gameTime || 0) < 25 ||
        alreadyHasPillar ||
        isEnemyLock ||
        threats.isPressedBack ||
        threats.enemyNearBaseCount > 0 ||
        threats.ownUnits.length < 2
      ) {
        score = -999;
      } else {
        score = 35 + pushRatio * 45;
      }
      return score;
    }
  },
  // Sul Experimental Metamorphosis (Emergency Board Reset when outnumbered/threatened)
  {
    id: 'experimentalMetamorphosis',
    matches: (card) => card.spellEffect === 'experimental_metamorphosis',
    score(ctx) {
      const { bot, card, cost } = ctx;
      let score = ctx.score;
      const b = bot.cardSystem.battlefield;
      let enemyMobileUnits = 0;
      let friendlyMobileUnits = 0;
      let enemyPlasmaValue = 0;
      let friendlyPlasmaValue = 0;
      let enemyNearBase = false;
      let hasHighThreatEnemy = false;

      if (b?.units) {
        const myBase = b.bases?.[bot.team];
        for (let ui = 0; ui < b.units.length; ui++) {
          const u = b.units[ui];
          if (u.isDead || u.isStructure || u.isGoldenStatue || u.isCocoon || u.isCinematic) continue;
          const unitCost = u.cardData?.cost || 10;
          const isHighThreat = unitCost >= 25 ||
            (u.hp || u.maxHp || 0) >= 250 ||
            Boolean(u.isHyper) ||
            Boolean(u.isTitan) ||
            Boolean(u.cardData?.isTitan) ||
            Boolean(u.name && u.name.startsWith('Hyper')) ||
            Boolean(u.cardData?.name && u.cardData.name.startsWith('Hyper')) ||
            Boolean(u.unitType && u.unitType.startsWith('Hyper'));

          if (u.team === bot.team) {
            friendlyMobileUnits++;
            friendlyPlasmaValue += unitCost;
          } else {
            enemyMobileUnits++;
            enemyPlasmaValue += unitCost;
            if (isHighThreat) {
              hasHighThreatEnemy = true;
            }
            if (myBase && Math.abs(u.x - myBase.x) < 400) {
              enemyNearBase = true;
            }
          }
        }
      }

      const isFodderSwarm = friendlyMobileUnits >= 4 && (friendlyPlasmaValue / Math.max(1, friendlyMobileUnits)) <= 8;
      const isEarlyGame = !isFodderSwarm && (bot.gameTime || 0) < 25 && !hasHighThreatEnemy && !(enemyNearBase && enemyPlasmaValue >= 25);

      // Strict gates: never cast Metamorphosis if:
      // 1. Match start / early game (< 25s) without high threat or base breach
      // 2. Empty enemy board
      // 3. Enemy army value < 35 plasma and no high-threat unit (neutralizing cheap units gifts 25-plasma Sul units to opponent!)
      // 4. Enemy unit count < 3 without high threat or base breach (plasma >= 20 near base)
      // 5. Friendly army already holds board lead (friendly >= enemy plasma) and base is not threatened (exempt if sacrificing fodder swarm)
      // 6. Friendly army is winning decisively (friendly > enemy * 1.2 with >= 2 units)
      const boardLeadBlocks = !isFodderSwarm && (
        (friendlyPlasmaValue >= enemyPlasmaValue && !enemyNearBase) ||
        (friendlyPlasmaValue > enemyPlasmaValue * 1.2 && friendlyMobileUnits >= 2)
      );

      if (
        isEarlyGame ||
        enemyMobileUnits === 0 ||
        (enemyPlasmaValue < 35 && !hasHighThreatEnemy && !(enemyNearBase && enemyPlasmaValue >= 20)) ||
        (enemyMobileUnits < 3 && !hasHighThreatEnemy && !(enemyNearBase && enemyPlasmaValue >= 20)) ||
        boardLeadBlocks
      ) {
        score = -999;
      } else {
        // Controlled situational utility (does not inherit raw 33 * 1.4 = 46.2 card cost inflation)
        let utility = 20;
        if (isFodderSwarm) utility += 45; // Transform expendable fodder into high-HP cocoons
        if (hasHighThreatEnemy) utility += 25;
        if (enemyNearBase) utility += 35;
        const plasmaAdvantage = Math.max(0, enemyPlasmaValue - friendlyPlasmaValue);
        utility += Math.min(30, plasmaAdvantage * 1.0);
        if (enemyMobileUnits > friendlyMobileUnits) {
          utility += Math.min(20, (enemyMobileUnits - friendlyMobileUnits) * 6);
        }
        score = utility;
      }
      return score;
    }
  },
  // Human Mental Extraction (Mirror Opponent's Hand when profitable)
  {
    id: 'mentalExtraction',
    matches: (card) => card.spellEffect === 'mental_extraction',
    score(ctx) {
      const { bot, hand } = ctx;
      let score = ctx.score;
      const oppHand = bot.belief.enemyHand();
      let oppValidCount = 0;
      let oppTotalCost = 0;
      for (const oc of oppHand) {
        if (oc && !oc.pending && !oc.hidden) {
          oppValidCount++;
          oppTotalCost += (oc.cost || 5);
        }
      }
      // If opponent has < 2 cards, never discard into an empty/near-empty hand
      if (oppValidCount < 2) {
        score = -999;
      } else {
        score += 15 + oppValidCount * 8 + (oppTotalCost / Math.max(1, oppValidCount)) * 1.5;
      }
      return score;
    }
  },
  // Human Fodder (Parachutes 8 Marines across the frontline)
  {
    id: 'fodder',
    matches: (card) => card.spellEffect === 'fodder',
    score(ctx) {
      const { bot, threats } = ctx;
      let score = ctx.score;
      let fodderScore = 30;
      if (threats.isPressedBack) fodderScore += 25 * bot.counterSensitivity;
      if (threats.enemyGroundCount >= 2) fodderScore += 20 * bot.counterSensitivity;
      const enemyHasHeavySplash = threats.enemyUnits.some(u => (u.splashRadius || 0) > 30 || (u.capsuleSplashRadius || 0) > 30);
      if (enemyHasHeavySplash) fodderScore -= 25 * bot.counterSensitivity;
      score += fodderScore;
      return score;
    }
  },
  // Sul Ambush (Spawn all units from hand in battle at the frontline)
  {
    id: 'ambush',
    matches: (card) => card.spellEffect === 'ambush' || card.name === 'Ambush',
    score(ctx) {
      const { bot, slot, hand, threats, nameCounts } = ctx;
      let score = ctx.score;
      if (!hand) return -999;
      let unitCount = 0;
      let totalUnitCost = 0;
      for (let s = 0; s < hand.length; s++) {
        if (s === slot) continue;
        const c = hand[s];
        if (c && !c.pending && !c.hidden && (c.type === 'Unit' || c.type === 'Melt' || c.isMelt) && !c.isSpell && !c.isStructure && !c.isModule && !c.isBuilding) {
          unitCount++;
          totalUnitCost += (c.cost || 0);
        }
      }
      // Rule A: Never cast Ambush with 0 units (absolute waste)
      if (unitCount === 0) return -999;

      // Rule B: If holding a pair waiting for triplet fusion and not under heavy pressure, hold for the 3rd copy
      if (bot.profile?.holdTriplets && !threats.isPressedBack) {
        for (const count of Object.values(nameCounts || {})) {
          if (count >= 2) return -999;
        }
      }

      // Rule C: Solo unit ambush is inefficient — hold and wait to draw more units unless base is threatened
      if (unitCount === 1 && !threats.isPressedBack) {
        return -999;
      }

      // Rule D: Scale reward with swarm size (2 to 4 units) and frontline pressure
      score += 30 + unitCount * 15 + (threats.isPressedBack ? 25 : 10);
      return score;
    }
  },
  // Human Reconsider (Discard hand, draw 5 new cards)
  {
    id: 'reconsider',
    matches: (card) => card.spellEffect === 'concentration',
    score(ctx) {
      const { bot, card, slot, hand, cost, nameCounts, hasFusibleNonPulpCard, pulpCount, threats, currentPlasma } = ctx;
      let score = ctx.score;
      // 1. Never discard when holding valuable pairs (or Pulp wildcard pairs)
      let hasValuablePair = false;
      if (bot.profile.holdTriplets) {
        for (const count of Object.values(nameCounts)) {
          if (count >= 2) {
            hasValuablePair = true;
            break;
          }
        }
        if (pulpCount >= 1 && hasFusibleNonPulpCard) {
          hasValuablePair = true;
        }
      }
      if (hasValuablePair) {
        score = -999;
      } else {
        // 2. Assess hand quality vs current tactical threats
        let handHasAirCounter = false;
        let otherCardsPlayable = 0;
        let otherCardsCount = 0;

        for (let s = 0; s < hand.length; s++) {
          const hc = hand[s];
          if (!hc || s === slot) continue;
          otherCardsCount++;
          if (hc.targetsAir !== false && !hc.isSpell) handHasAirCounter = true;
          if (currentPlasma >= bot.cardSystem.getEffectiveCost(bot.team, hc)) otherCardsPlayable++;
        }

        const desperateForAir = threats.enemyAirUnits > 0 && threats.ownAirTargeters < threats.enemyAirUnits && !handHasAirCounter;
        const stuckUnderPressure = threats.isPressedBack && otherCardsPlayable === 0 && otherCardsCount >= 2;
        const staleHand = otherCardsCount >= 3 && otherCardsPlayable === 0;

        if (desperateForAir) {
          score = 45 * bot.counterSensitivity;
        } else if (stuckUnderPressure) {
          score = 35 * bot.counterSensitivity;
        } else if (staleHand) {
          score = 25;
        } else if (otherCardsCount <= 1 && currentPlasma >= cost) {
          score = 20; // Hand nearly empty: fast 5-card reload
        } else {
          score = -999;
        }
      }
      return score;
    }
  },
  // Human Hacker: Cyber Harpoon & Dual-Hacker Clash (Hyper Transcendent Counter)
  {
    id: 'hacker',
    matches: (card) => card.name === 'Hacker' || card.unitType === 'Hacker',
    score(ctx) {
      const { bot, threats } = ctx;
      let score = ctx.score;

      // 1. Prime Counter: Enemy has an active Hacker on the field, or our ally is hacked!
      // Deploying Hacker immediately triggers Hyper Transcendent XLink on both Hackers,
      // neutralizes the enemy Hacker, and releases the disabled friendly ally.
      if (threats.enemyHasHacker || (threats.ownDisabledByHackerCount || 0) > 0) {
        score += 65 * bot.counterSensitivity;
        if ((threats.ownDisabledByHackerCount || 0) > 0) {
          score += 35 * bot.counterSensitivity;
        }
        if ((threats.ownDisabledHeavyByHackerCount || 0) > 0) {
          score += 20 * bot.counterSensitivity;
        }
        return score;
      }

      // 2. Proactive memory counter: opponent recently fielded a Hacker (< 25s)
      if (threats.recentHacker) {
        score += 25 * bot.counterSensitivity;
      }

      // 3. General utility: Harpoon high-value heavy enemy units to disable them
      if (threats.enemyArmoredUnits >= 1) {
        score += 18 * bot.counterSensitivity;
      }

      // 4. Cadence / restraint: If bot already has an active Hacker on the board,
      // don't spam duplicate Hackers.
      if ((threats.ownHackerCount || 0) >= 1) {
        score -= 30;
      }

      // 5. If enemy has no mobile units to hack, Hacker has no valid target
      const mobileEnemyCount = (threats.enemyGroundCount || 0) + (threats.enemyAirUnits || 0);
      if (mobileEnemyCount === 0) {
        score -= 35;
      }

      return score;
    }
  },
  // Sul Retreat Protocol Tactical Decision
  {
    id: 'retreatProtocol',
    matches: (card) => card.spellEffect === 'retreat_protocol' || card.name === 'Retreat Protocol',
    score(ctx) {
      const { bot } = ctx;
      const b = bot.cardSystem?.battlefield || bot.battlefield;
      let friendlyUnitsCount = 0;
      let friendlyMobilePlasma = 0;
      let endangeredUnits = 0;

      if (b?.units) {
        for (let i = 0; i < b.units.length; i++) {
          const u = b.units[i];
          if (u.team === bot.team && !u.isDead && !u.isStructure && !u.isBuilding && !u.isBase && !u.isBastionRearGuard) {
            friendlyUnitsCount++;
            const cost = u.cardData?.cost || 0;
            friendlyMobilePlasma += cost;
            if (u.hp < u.maxHp * 0.6 || u.underFireTimer > 0) {
              endangeredUnits++;
            }
          }
        }
      }

      // Hard gate: Never cast if 0 friendly units on field or negligible army value
      if (friendlyUnitsCount === 0 || friendlyMobilePlasma < 40) {
        return -999;
      }

      let score = ctx.score;
      // High score if we have substantial plasma value on field and our units are endangered
      if (friendlyMobilePlasma >= 60 && endangeredUnits >= 2) {
        score += 25 + friendlyMobilePlasma * 0.2;
      } else if (friendlyMobilePlasma >= 80) {
        score += 15;
      } else {
        score -= 20;
      }
      return score;
    }
  }
];

// Phase 2: after the generic counter bonuses 1-4d, once the scorer has the
// enemy's grounded count at hand (ctx.enemyGroundedGround).
export const SITUATIONAL_RULES = [
  // 4e. Anti-Siege & Anti-Fortress Counters (Precision Strike & Air Bypass)
  {
    id: 'precisionStrike',
    matches: (card) => card.spellEffect === 'precision_strike' || card.name === 'Precision Strike',
    score(ctx) {
      const { bot, cost, threats, enemyGroundedGround } = ctx;
      let score = ctx.score;
      if (threats.hasEnemyParachuting) {
        // Parachuting soldiers are immune to damage - wait until they touch down before dropping strike!
        score = -999;
      } else if (threats.enemyGroundCount === 0 && threats.enemyStructuresCount === 0) {
        score -= 30; // Don't drop on an empty board
      } else {
        // Validate what unit is ACTUALLY closest to territoryDivider (the true Precision Strike target)
        const b = bot.cardSystem?.battlefield || bot.battlefield;
        const dividerX = (b?.territoryDivider !== undefined && b?.territoryDivider !== null)
          ? b.territoryDivider
          : ((b?.width || 1600) / 2);
        let nearestTarget = null;
        let minDist = Infinity;
        let hasPositionedUnits = false;

        for (const eu of (threats.enemyUnits || [])) {
          if (eu.isAir || eu.isCloaked || eu.isAntiShade || eu.isParachuting || eu.isBase || eu.isGoldenStatue || eu.isCocoon) continue;
          if (typeof eu.x === 'number') {
            hasPositionedUnits = true;
            const d = Math.abs(eu.x - dividerX);
            if (d < minDist) {
              minDist = d;
              nearestTarget = eu;
            }
          }
        }

        if (hasPositionedUnits && nearestTarget) {
          let splashClusterValue = 0;
          let hitsPriorityTarget = false;
          for (const eu of (threats.enemyUnits || [])) {
            if (eu.isAir || eu.isCloaked || eu.isParachuting) continue;
            if (typeof eu.x === 'number' && Math.abs(eu.x - nearestTarget.x) <= 55) {
              const uCost = eu.cardData?.cost || eu.cost || 6;
              splashClusterValue += uCost;
              const euDps = (eu.damage || 0) / Math.max(0.25, eu.attackSpeed || 1);
              if (
                eu.isStructure || eu.cardData?.isStructure ||
                uCost >= 14 ||
                ((eu.damage || 0) >= 50 || euDps >= 20) ||
                (eu.deflectionRating && eu.deflectionRating >= 0.20)
              ) {
                hitsPriorityTarget = true;
              }
            }
          }
          if (!hitsPriorityTarget && splashClusterValue < 10) {
            // Avoid wasting a 10-plasma Precision Strike on a lone 5-cost Marine screening at the midline!
            score -= 40;
          } else if (threats.hasEnemySiegeLine || threats.enemyStructuresCount > 0) {
            score += 40 * bot.counterSensitivity;
          } else if (threats.enemyArmoredUnits > 0 || enemyGroundedGround >= 2 || splashClusterValue >= 14) {
            score += 20 * bot.counterSensitivity;
          }
        } else {
          if (threats.hasEnemySiegeLine || threats.enemyStructuresCount > 0) {
            score += 40 * bot.counterSensitivity;
          } else if (threats.enemyArmoredUnits > 0 || enemyGroundedGround >= 2) {
            score += 20 * bot.counterSensitivity;
          }
        }
      }
      return score;
    }
  }
];

// Precision Strike is also special-cased inside the generic glass-cannon
// bonus; one definition of "that card" for both places.
export function isPrecisionStrike(card) {
  return card.spellEffect === 'precision_strike' || card.name === 'Precision Strike';
}
